import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import SearchableSelect from '../components/SearchableSelect';
import {
  ArrowRightLeft, Plus, Clock, CheckCircle2, ShoppingCart, AlertTriangle,
  Eye, Search, Loader2, Calendar, History, ChevronLeft, ChevronRight,
  Wrench, Package, Trash2, Pencil, ShieldCheck,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_META = {
  pending_replacement: { label: 'Needs Replacement', color: 'bg-red-100 text-red-700 border-red-200' },
  pr_raised:           { label: 'PR Raised',         color: 'bg-blue-100 text-blue-700 border-blue-200' },
  replaced:            { label: 'Replaced',           color: 'bg-green-100 text-green-700 border-green-200' },
  cancelled:           { label: 'Cancelled',          color: 'bg-slate-100 text-slate-500 border-slate-200' },
};
const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, color: 'bg-slate-100 text-slate-600 border-slate-200' };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-semibold ${m.color}`}>{m.label}</span>;
};

// ── Asset helpers ─────────────────────────────────────────────────────────────
const TRUCK_TYPES = ['truck', 'lmv', 'dumper'];
const isTruck = a => TRUCK_TYPES.some(t => (a.asset_type || '').toLowerCase().includes(t));

// ── Log Cannibalization Modal ─────────────────────────────────────────────────
const LogModal = ({ open, onClose, onSuccess, assets = [], inventory = [] }) => {
  const guardRef = useRef(false);
  const blank = {
    donor_asset: '', recipient_asset: '', section: 'truck',
    date: new Date().toISOString().split('T')[0], reason: '',
    parts: [],
  };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [partRow, setPartRow] = useState({ sku_id: '', part_name: '', quantity: 1 });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const sectionAssets = assets.filter(a => form.section === 'truck' ? isTruck(a) : !isTruck(a));

  const addPart = () => {
    if (!partRow.sku_id && !partRow.part_name.trim()) { toast.error('Enter a part name or select from inventory'); return; }
    const sku = inventory.find(s => s.id === partRow.sku_id);
    set('parts', [...form.parts, {
      sku_id: partRow.sku_id,
      part_name: sku?.particulars || partRow.part_name,
      quantity: partRow.quantity,
    }]);
    setPartRow({ sku_id: '', part_name: '', quantity: 1 });
  };

  const removePart = idx => set('parts', form.parts.filter((_, i) => i !== idx));

  const submit = async e => {
    e.preventDefault();
    if (guardRef.current) return;
    if (form.parts.length === 0) { toast.error('Add at least one part'); return; }
    if (form.donor_asset === form.recipient_asset) { toast.error('Donor and recipient must be different assets'); return; }
    guardRef.current = true;
    setSaving(true);
    try {
      await axios.post(`${API}/cannibalization`, form, { headers: authH() });
      toast.success('Cannibalization logged & stores notified');
      setForm(blank);
      setPartRow({ sku_id: '', part_name: '', quantity: 1 });
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save record');
    } finally {
      guardRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-orange-500" /> Log Cannibalization
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Section</Label>
                <Select value={form.section} onValueChange={v => { set('section', v); set('donor_asset', ''); set('recipient_asset', ''); }}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="truck">Truck</SelectItem>
                    <SelectItem value="machinery">Machinery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input type="date" className="mt-1" value={form.date} onChange={e => set('date', e.target.value)} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Donor Asset (part taken FROM) *</Label>
                <SearchableSelect
                  value={form.donor_asset}
                  onValueChange={v => set('donor_asset', v)}
                  options={sectionAssets.map(a => ({ value: a.door_no, label: `${a.door_no} — ${a.make || ''} ${a.model || ''}`.trim() }))}
                  placeholder="Select donor asset"
                  className="mt-1"
                  data-testid="donor-asset-select"
                />
              </div>
              <div>
                <Label>Recipient Asset (part put INTO) *</Label>
                <SearchableSelect
                  value={form.recipient_asset}
                  onValueChange={v => set('recipient_asset', v)}
                  options={sectionAssets.filter(a => a.door_no !== form.donor_asset).map(a => ({ value: a.door_no, label: `${a.door_no} — ${a.make || ''} ${a.model || ''}`.trim() }))}
                  placeholder="Select recipient asset"
                  className="mt-1"
                  data-testid="recipient-asset-select"
                />
              </div>
            </div>

            {/* Parts section */}
            <div className="border rounded-lg p-4 space-y-3">
              <Label className="text-base font-semibold">Parts Cannibalized</Label>
              <div className="flex gap-2 items-end flex-wrap">
                <div className="flex-1 min-w-[160px]">
                  <Label className="text-xs text-slate-500">Select from Inventory</Label>
                  <SearchableSelect
                    value={partRow.sku_id}
                    onValueChange={v => setPartRow(p => ({ ...p, sku_id: v }))}
                    options={inventory.map(s => ({ value: s.id, label: `${s.particulars} (${s.part_number || 'no PN'})` }))}
                    placeholder="Select part"
                    className="mt-1"
                  />
                </div>
                <div className="w-32">
                  <Label className="text-xs text-slate-500">Or enter name</Label>
                  <Input
                    placeholder="Part name"
                    value={partRow.part_name}
                    onChange={e => setPartRow(p => ({ ...p, part_name: e.target.value, sku_id: '' }))}
                    className="mt-1"
                    data-testid="part-name-input"
                  />
                </div>
                <div className="w-20">
                  <Label className="text-xs text-slate-500">Qty</Label>
                  <Input
                    type="number" min={1}
                    value={partRow.quantity}
                    onChange={e => setPartRow(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))}
                    className="mt-1"
                  />
                </div>
                <Button type="button" variant="outline" onClick={addPart} className="mt-5">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {form.parts.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Part Name</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.parts.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell>{p.part_name}</TableCell>
                        <TableCell className="text-right">{p.quantity}</TableCell>
                        <TableCell>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removePart(i)} className="text-red-600 h-7">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-xs text-slate-400 italic text-center py-2">No parts added yet</p>
              )}
            </div>

            <div>
              <Label>Reason / Notes *</Label>
              <Textarea className="mt-1" rows={3}
                placeholder="Why was this cannibalization done? (e.g. VT-25 is critical for production today)"
                value={form.reason} onChange={e => set('reason', e.target.value)} required />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
              A parts-required ticket for <strong>{form.donor_asset || 'the donor asset'}</strong> will be
              automatically raised in Stores. Track it here until the replacement part is received.
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 mt-2 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : 'Log & Notify Stores'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Edit Modal (admin only) ────────────────────────────────────────────────────
const EditModal = ({ record, open, onClose, onSuccess, assets = [], inventory = [] }) => {
  const guardRef = useRef(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [partRow, setPartRow] = useState({ sku_id: '', part_name: '', quantity: 1 });

  useEffect(() => {
    if (record) {
      setForm({
        donor_asset: record.donor_asset || '',
        recipient_asset: record.recipient_asset || '',
        section: record.section || 'truck',
        date: record.date || '',
        reason: record.reason || '',
        parts: record.parts?.length ? record.parts : [{ part_name: record.part_name, sku_id: record.sku_id || '', quantity: record.quantity || 1 }],
      });
    }
  }, [record]);

  if (!record) return null;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const sectionAssets = assets.filter(a => form.section === 'truck' ? isTruck(a) : !isTruck(a));

  const addPart = () => {
    if (!partRow.sku_id && !partRow.part_name.trim()) return;
    const sku = inventory.find(s => s.id === partRow.sku_id);
    set('parts', [...(form.parts || []), { sku_id: partRow.sku_id, part_name: sku?.particulars || partRow.part_name, quantity: partRow.quantity }]);
    setPartRow({ sku_id: '', part_name: '', quantity: 1 });
  };

  const submit = async e => {
    e.preventDefault();
    if (guardRef.current) return;
    guardRef.current = true;
    setSaving(true);
    try {
      await axios.put(`${API}/cannibalization/${record.id}`, form, { headers: authH() });
      toast.success('Record updated');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      guardRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl flex flex-col max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4" /> Edit — {record.record_number}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Donor Asset</Label>
                <SearchableSelect value={form.donor_asset} onValueChange={v => set('donor_asset', v)}
                  options={sectionAssets.map(a => ({ value: a.door_no, label: `${a.door_no} — ${a.make || ''} ${a.model || ''}`.trim() }))}
                  placeholder="Donor asset" className="mt-1" />
              </div>
              <div>
                <Label>Recipient Asset</Label>
                <SearchableSelect value={form.recipient_asset} onValueChange={v => set('recipient_asset', v)}
                  options={sectionAssets.map(a => ({ value: a.door_no, label: `${a.door_no} — ${a.make || ''} ${a.model || ''}`.trim() }))}
                  placeholder="Recipient asset" className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input type="date" className="mt-1" value={form.date || ''} onChange={e => set('date', e.target.value)} />
              </div>
              <div>
                <Label>Section</Label>
                <Select value={form.section} onValueChange={v => set('section', v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="truck">Truck</SelectItem>
                    <SelectItem value="machinery">Machinery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Parts */}
            <div className="border rounded-lg p-3 space-y-3">
              <Label className="font-semibold">Parts</Label>
              <div className="flex gap-2 items-end flex-wrap">
                <div className="flex-1 min-w-[130px]">
                  <Input placeholder="Part name" value={partRow.part_name}
                    onChange={e => setPartRow(p => ({ ...p, part_name: e.target.value, sku_id: '' }))} className="mt-1" />
                </div>
                <div className="w-20">
                  <Input type="number" min={1} value={partRow.quantity}
                    onChange={e => setPartRow(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))} className="mt-1" />
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addPart}><Plus className="h-4 w-4" /></Button>
              </div>
              {(form.parts || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm bg-slate-50 px-3 py-2 rounded">
                  <span>{p.part_name} × {p.quantity}</span>
                  <button type="button" onClick={() => set('parts', form.parts.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea className="mt-1" rows={2} value={form.reason || ''} onChange={e => set('reason', e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 mt-2 border-t">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-slate-800 hover:bg-slate-900">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Raise PR Modal (admin only) ───────────────────────────────────────────────
const RaisePRModal = ({ record, open, onClose, onSuccess }) => {
  const guardRef = useRef(false);
  const [urgency, setUrgency] = useState('urgent');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (guardRef.current) return;
    guardRef.current = true;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/cannibalization/${record.id}/raise-pr`,
        { urgency }, { headers: authH() });
      toast.success(`${res.data.pr_numbers} Purchase Requisition(s) raised — Approved by you`);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to raise PR');
    } finally {
      guardRef.current = false;
      setSaving(false);
    }
  };

  if (!record) return null;
  const parts = record.parts?.length ? record.parts : [{ part_name: record.part_name, quantity: record.quantity }];
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-blue-600" /> Approve & Raise PR
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
            <p><span className="text-slate-500">Donor:</span> <strong>{record.donor_asset}</strong></p>
            <p><span className="text-slate-500">Parts:</span>
              {parts.map((p, i) => <span key={i} className="ml-1 text-slate-700">{p.part_name} ×{p.quantity}{i < parts.length - 1 ? ',' : ''}</span>)}
            </p>
            <p><span className="text-slate-500">Record:</span> {record.record_number}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-700">
            <ShieldCheck className="inline h-3 w-3 mr-1" />
            Your name will be recorded as the approving admin on this request.
          </div>
          <div>
            <Label>Urgency</Label>
            <Select value={urgency} onValueChange={setUrgency}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-raise-pr-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShoppingCart className="h-4 w-4 mr-2" />}
            Approve & Raise PR
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Resolve Modal (admin only) ────────────────────────────────────────────────
const ResolveModal = ({ record, open, onClose, onSuccess }) => {
  const guardRef = useRef(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (guardRef.current) return;
    guardRef.current = true;
    setSaving(true);
    try {
      await axios.post(`${API}/cannibalization/${record.id}/resolve`, { notes }, { headers: authH() });
      toast.success('Marked as replaced — parts ticket closed');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resolve');
    } finally {
      guardRef.current = false;
      setSaving(false);
    }
  };

  if (!record) return null;
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" /> Mark as Replaced
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm space-y-1">
            <p><span className="text-slate-500">Asset:</span> <strong>{record?.donor_asset}</strong></p>
            <p><span className="text-slate-500">Parts:</span> {(record?.parts?.length ? record.parts.map(p => p.part_name).join(', ') : record?.part_name)}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-700">
            <ShieldCheck className="inline h-3 w-3 mr-1" />
            Your name will be recorded as the approving admin.
          </div>
          <div>
            <Label>Resolution Notes (optional)</Label>
            <Textarea className="mt-1" rows={2}
              placeholder="e.g. New part installed on 11 Mar 2026"
              value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-resolve-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Confirm Replaced
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Detail Modal ──────────────────────────────────────────────────────────────
const DetailModal = ({ record, open, onClose }) => {
  if (!record) return null;
  const fmt = d => d ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false }) : '—';
  const parts = record.parts?.length ? record.parts : (record.part_name ? [{ part_name: record.part_name, quantity: record.quantity }] : []);
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-orange-500" /> {record.record_number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2.5">
            {[
              ['Date',            record.date],
              ['Section',         record.section],
              ['Donor Asset',     record.donor_asset],
              ['Recipient Asset', record.recipient_asset],
              ['Status',          null],
              ['Logged At',       fmt(record.created_at)],
              ...(record.resolved_at ? [['Resolved At', fmt(record.resolved_at)]] : []),
            ].map(([label, val]) => (
              label === 'Status' ? (
                <div key="status" className="bg-slate-50 rounded-lg p-2.5">
                  <p className="text-xs text-slate-500 mb-1">Status</p>
                  <StatusBadge status={record.status} />
                </div>
              ) : (
                <div key={label} className="bg-slate-50 rounded-lg p-2.5">
                  <p className="text-xs text-slate-500 mb-0.5">{label}</p>
                  <p className="font-medium text-slate-800">{val || '—'}</p>
                </div>
              )
            ))}
          </div>

          {/* Parts list */}
          {parts.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-3 py-2 border-b">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Parts ({parts.length})</p>
              </div>
              {parts.map((p, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 text-sm border-b last:border-0">
                  <span>{p.part_name}</span>
                  <span className="text-slate-500 text-xs bg-slate-100 px-2 py-0.5 rounded">×{p.quantity}</span>
                </div>
              ))}
            </div>
          )}

          {/* Audit trail */}
          <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Audit Trail</p>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-500 w-24">Raised by:</span>
              <span className="font-medium text-slate-800">{record.raised_by || record.created_by || '—'}</span>
            </div>
            {record.approved_by && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-500 w-24">Approved by:</span>
                <span className="font-medium text-blue-700 flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" /> {record.approved_by}
                </span>
              </div>
            )}
            {record.resolved_by && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-500 w-24">Resolved by:</span>
                <span className="font-medium text-green-700 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> {record.resolved_by}
                </span>
              </div>
            )}
          </div>

          {record.reason && (
            <div className="border rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1 font-semibold uppercase tracking-wide">Reason</p>
              <p className="text-slate-700">{record.reason}</p>
            </div>
          )}
          {record.notes && (
            <div className="border rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1 font-semibold uppercase tracking-wide">Resolution Notes</p>
              <p className="text-slate-700">{record.notes}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function CannibalizationPage({ user }) {
  const isAdmin = user?.is_admin || user?.is_super_admin;

  const [tab, setTab] = useState('outstanding');
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const LIMIT = 30;

  const [assets, setAssets] = useState([]);
  const [inventory, setInventory] = useState([]);

  const [assetFilter, setAssetFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [showLog, setShowLog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [raisePR, setRaisePR] = useState(null);
  const [resolving, setResolving] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const deleteGuard = useRef(false);

  // Fetch assets and inventory on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const h = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/assets?limit=300`, { headers: h }).then(r => setAssets(r.data || [])).catch(() => {});
    axios.get(`${API}/inventory/skus?limit=500`, { headers: h }).then(r => setInventory(r.data?.skus || r.data || [])).catch(() => {});
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cannibalization/stats`, { headers: authH() });
      setStats(r.data);
    } catch { /* silent */ }
  }, []);

  const fetchRecords = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: LIMIT };
      if (assetFilter.trim()) params.asset = assetFilter.trim();
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      if (tab === 'outstanding') {
        const [a, b] = await Promise.all([
          axios.get(`${API}/cannibalization`, { headers: authH(), params: { ...params, status: 'pending_replacement' } }),
          axios.get(`${API}/cannibalization`, { headers: authH(), params: { ...params, status: 'pr_raised' } }),
        ]);
        const merged = [...(a.data.records || []), ...(b.data.records || [])];
        merged.sort((x, y) => new Date(y.created_at) - new Date(x.created_at));
        setRecords(merged);
        setTotal(a.data.total + b.data.total);
      } else {
        const r = await axios.get(`${API}/cannibalization`, { headers: authH(), params });
        setRecords(r.data.records || []);
        setTotal(r.data.total || 0);
      }
    } catch {
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [tab, assetFilter, startDate, endDate]);

  useEffect(() => { fetchStats(); fetchRecords(1); setPage(1); }, [fetchStats, fetchRecords]);

  const refresh = () => { fetchStats(); fetchRecords(1); setPage(1); };
  const totalPages = Math.ceil(total / LIMIT);

  const handleDelete = async (record) => {
    if (deleteGuard.current) return;
    deleteGuard.current = true;
    try {
      await axios.delete(`${API}/cannibalization/${record.id}`, { headers: authH() });
      toast.success(`${record.record_number} deleted`);
      setDeleting(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      deleteGuard.current = false;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-orange-600 flex items-center justify-center">
            <ArrowRightLeft className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Cannibalization Tracker</h1>
            <p className="text-sm text-slate-500">Track parts stripped from assets — pending until replacement arrives</p>
          </div>
        </div>
        <Button onClick={() => setShowLog(true)} className="bg-orange-600 hover:bg-orange-700 text-white gap-2"
          data-testid="log-cannibalization-btn">
          <Plus className="h-4 w-4" /> Log Cannibalization
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Needs Replacement', value: stats.pending_replacement ?? 0, icon: AlertTriangle, color: 'bg-red-500' },
          { label: 'PR Raised',         value: stats.pr_raised ?? 0,           icon: ShoppingCart,  color: 'bg-blue-500' },
          { label: 'Replaced This Month', value: stats.replaced_month ?? 0,    icon: CheckCircle2,  color: 'bg-green-500' },
          { label: 'Total Records',     value: stats.total ?? 0,               icon: History,       color: 'bg-slate-500' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
            <div className={`h-9 w-9 rounded-lg ${s.color} flex items-center justify-center flex-shrink-0`}>
              <s.icon className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{s.value}</p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {[
          { key: 'outstanding', label: `Outstanding (${stats.outstanding ?? 0})`, icon: Clock },
          { key: 'history',     label: 'Full History', icon: History },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-800'
            }`} data-testid={`tab-${t.key}`}>
            <t.icon className="h-4 w-4" />{t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={assetFilter} onChange={e => setAssetFilter(e.target.value)}
              placeholder="Search asset (donor or recipient)..." className="pl-9" data-testid="filter-asset" />
          </div>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="pl-9" data-testid="filter-start-date" />
          </div>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="pl-9" data-testid="filter-end-date" />
          </div>
        </div>
        {(assetFilter || startDate || endDate) && (
          <button onClick={() => { setAssetFilter(''); setStartDate(''); setEndDate(''); }}
            className="mt-2 text-xs text-slate-500 hover:text-slate-700 underline">Clear filters</button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
        ) : records.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-3">
            <ArrowRightLeft className="h-12 w-12" />
            <p className="font-medium">{tab === 'outstanding' ? 'No outstanding cannibalized parts' : 'No records found'}</p>
            <p className="text-sm">{tab === 'outstanding' ? 'All replaced — great job!' : 'Log your first cannibalization to start tracking'}</p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Record No.</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Date</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">
                    <span className="flex items-center gap-1"><Wrench className="h-3.5 w-3.5 text-red-400" /> Donor (From)</span>
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">
                    <span className="flex items-center gap-1"><Package className="h-3.5 w-3.5 text-green-500" /> Recipient (To)</span>
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Parts</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Raised By</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Approved By</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {records.map(r => {
                  const parts = r.parts?.length ? r.parts : (r.part_name ? [{ part_name: r.part_name, quantity: r.quantity }] : []);
                  return (
                    <tr key={r.id} className="hover:bg-slate-50 transition-colors" data-testid={`cann-row-${r.id}`}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{r.record_number}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{r.date}</td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded text-xs">{r.donor_asset}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded text-xs">{r.recipient_asset}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-800 max-w-[160px]">
                        {parts.length === 1
                          ? <span>{parts[0].part_name} ×{parts[0].quantity}</span>
                          : <span>{parts[0].part_name} <span className="text-xs text-slate-400">+{parts.length - 1} more</span></span>
                        }
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{(r.raised_by || r.created_by || '—').split('@')[0]}</td>
                      <td className="px-4 py-3 text-xs">
                        {r.approved_by
                          ? <span className="text-blue-700 flex items-center gap-1"><ShieldCheck className="h-3 w-3" />{r.approved_by.split('@')[0]}</span>
                          : <span className="text-slate-300">—</span>
                        }
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setViewing(r)}
                            className="h-8 text-slate-500 hover:text-blue-600" data-testid={`view-btn-${r.id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          {isAdmin && r.status === 'pending_replacement' && (
                            <Button variant="ghost" size="sm" onClick={() => setRaisePR(r)}
                              className="h-8 text-blue-600 hover:bg-blue-50 text-xs px-2" data-testid={`raise-pr-btn-${r.id}`}>
                              <ShoppingCart className="h-3.5 w-3.5 mr-1" /> Raise PR
                            </Button>
                          )}
                          {isAdmin && (r.status === 'pending_replacement' || r.status === 'pr_raised') && (
                            <Button variant="ghost" size="sm" onClick={() => setResolving(r)}
                              className="h-8 text-green-600 hover:bg-green-50 text-xs px-2" data-testid={`resolve-btn-${r.id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Resolved
                            </Button>
                          )}
                          {isAdmin && (
                            <Button variant="ghost" size="sm" onClick={() => setEditing(r)}
                              className="h-8 text-slate-500 hover:text-slate-800" data-testid={`edit-btn-${r.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button variant="ghost" size="sm" onClick={() => setDeleting(r)}
                              className="h-8 text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-btn-${r.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                <p className="text-xs text-slate-500">Page {page} of {totalPages} · {total} records</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setPage(p => p - 1); fetchRecords(page - 1); }}
                    disabled={page === 1}><ChevronLeft className="h-4 w-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => { setPage(p => p + 1); fetchRecords(page + 1); }}
                    disabled={page === totalPages}><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirm */}
      <Dialog open={!!deleting} onOpenChange={v => { if (!v) setDeleting(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-red-700">Delete Record?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Delete <strong>{deleting?.record_number}</strong>? This will also remove the associated parts release ticket.
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button onClick={() => handleDelete(deleting)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modals */}
      <LogModal open={showLog} onClose={() => setShowLog(false)} onSuccess={refresh} assets={assets} inventory={inventory} />
      <EditModal record={editing} open={!!editing} onClose={() => setEditing(null)} onSuccess={refresh} assets={assets} inventory={inventory} />
      <RaisePRModal record={raisePR} open={!!raisePR} onClose={() => setRaisePR(null)} onSuccess={refresh} />
      <ResolveModal record={resolving} open={!!resolving} onClose={() => setResolving(null)} onSuccess={refresh} />
      <DetailModal record={viewing} open={!!viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
